<template>
    <div ref="patientCard" class="col-md-4 mb-4 patient-card">
        <div
class="card h-100 text-center position-relative" :data-id="patientData.id"
            style="cursor: pointer; transition: transform 0.2s ease, box-shadow 0.2s ease;"
            @click="openPatientRecord(patientData)">
            <div class="card-header pb-0">
                <div class="dropdown position-absolute top-0 end-0" @click.stop>
                    <button
id="navbarDropdownMenuLink" class="btn btn-link text-secondary" data-bs-toggle="dropdown"
                        aria-haspopup="true" aria-expanded="false" @click.stop><i
class="text-lg fa fa-ellipsis-v"
                            aria-hidden="true"></i>
                    </button>
                    <div
class="dropdown-menu dropdown-menu-end" aria-labelledby="navbarDropdownMenuLink" style=""
                        @click.stop>
                        <a
class="dropdown-item border-radius-md" href="javascript:;"
                            @click.stop="openPatientRecord(patientData)">Abrir Expediente</a>
                        <a class="dropdown-item border-radius-md" href="javascript:;" @click.stop>Enviar perfil por
                            WhatsApp</a>
                        <a class="dropdown-item border-radius-md" href="javascript:;" @click.stop>Enviar bienvenida por
                            WhatsApp</a>
                        <a
class="dropdown-item border-radius-md" href="javascript:;"
                            @click.stop="openEditPatientModal">Actualizar</a>
                        <a
class="dropdown-item border-radius-md" href="javascript:;"
                            @click.stop="$emit('deleteRecord', { id: patientData.id, fullname: `${patientData.last_name} ${patientData.first_name}` })">Eliminar</a>
                    </div>
                </div>
            </div>
            <!-- Imagen del paciente -->
            <div class="position-relative d-inline-block mx-auto" style="margin-top: 15px;">
                <img
:src="getPatientPhotoUrl" alt="Patient Image" class="card-img-top rounded-circle"
                    style="width: 80px; height: 80px; object-fit: cover;">
                <!-- Badge con ID del paciente -->
                <span
class="badge bg-gradient-primary position-absolute bottom-0 end-0 rounded-pill"
                    style="font-size: 0.65rem; padding: 0.25rem 0.5rem;">
                    #{{ patientData.id }}
                </span>
            </div>

            <div class="card-body">
                <h6 class="mb-0">{{ patientData.last_name }}</h6>
                <h5 class="compact-text">{{ patientData.first_name }}</h5>
                <!-- Contenedor de iconos -->
                <div class="icon-container" @click.stop>
                    <div class="icon-text" @click.stop="toggleText('email')">
                        <i class="fa-regular fa-circle-info"></i>
                    </div>
                    <div class="icon-text" @click.stop="toggleText('gender')">
                        <i class="fa-duotone fa-solid fa-person-half-dress"></i>
                    </div>
                    <div class="icon-text" @click.stop="toggleText('whatsapp')">
                        <i class="fa-brands fa-whatsapp"></i>
                    </div>
                </div>

                <!-- Mostrar texto relacionado al hacer clic -->
                <div class="text-container">
                    <p v-if="visibleField === 'email'">{{ patientData.email }}</p>
                    <p v-if="visibleField === 'gender'">{{ getGender(patientData.gender) }}</p>
                    <p v-if="visibleField === 'whatsapp'">{{ patientData.whatsapp }}</p>
                </div>
            </div>
        </div>

        <!-- Usar teleport para sacar el modal del flujo del DOM -->
        <teleport to="body">
            <PatientModal
v-model="showEditModal" :is-edit-mode="true" :patient-data="patientData"
                @entity-updated="onPatientUpdated" />
        </teleport>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { getGenderName } from '@/utils/globalFunctions.js';
import PatientModal from '@/components/PatientModal.vue';
import { useStore } from '@/store/Expedientes/pacientes.js';
import { patientEventBus, updatePatientData } from '@/utils/eventBus.js';
import profilePicture from '@/assets/img/profile-picture.png';
import profilePictureWomen from '@/assets/img/profile-picture-women.png';

const showEditModal = ref(false);
const router = useRouter();
const { addPatientData, getPatientDataById } = useStore();
let unsubscribe = null;

const props = defineProps({
    data: Object,
});

// Crear una copia reactiva de los datos del paciente para evitar mutación de props
const patientData = reactive({ ...props.data });

// ⚡ SOLUCIÓN: Si photo_url no viene en props.data, intentar cargar desde localStorage
if (!patientData.photo_url) {
    const storedPatient = getPatientDataById(props.data.id);
    if (storedPatient?.photo_url) {
        patientData.photo_url = storedPatient.photo_url;
    }
}

const visibleField = ref(null);

const getGender = (genderCode) => {
    return getGenderName(genderCode);
};

// Función para obtener la imagen de perfil según el género
const getProfilePicture = (gender) => {
    return (gender === 'F' || gender === 'female') ? profilePictureWomen : profilePicture;
};

// Computed para obtener la URL de la foto del paciente (con photo_url o por defecto según género)
// ✅ Usar computed en lugar de función para asegurar reactividad
const getPatientPhotoUrl = computed(() => {
    return patientData.photo_url || patientData.imageUrl || getProfilePicture(patientData.gender);
});

watch(
    () => patientData.photo_url,
    (newUrl, oldUrl) => {
        if (newUrl === oldUrl) {
            return;
        }
        console.log('🖼️ PatientCard: photo_url actualizado para paciente', props.data.id, 'de', oldUrl, 'a', newUrl);
    }
);

// Función para abrir el modal en modo edición
const openEditPatientModal = () => {
    showEditModal.value = true;
};

// Función para mostrar el texto correspondiente
const toggleText = (field) => {
    visibleField.value = field;
    setTimeout(() => {
        visibleField.value = null;
    }, 15000); // El texto desaparecerá después de 15 segundos
};

// Función para abrir el expediente del paciente
const openPatientRecord = (data) => {
    addPatientData(data);
    router.push({ name: 'Expedientes' });
};

// Función para manejar cuando se actualiza el paciente desde el modal
const onPatientUpdated = (updatedData) => {
    console.log('📝 Patient updated from modal:', updatedData);

    // Solo actualizar el store una vez para evitar bucles
    addPatientData(updatedData);

    // Actualizar los datos locales directamente (sin volver a notificar)
    const fieldsToUpdate = ['first_name', 'last_name', 'gender', 'birthdate', 'email', 'whatsapp'];
    fieldsToUpdate.forEach(field => {
        if (updatedData[field] !== undefined) {
            patientData[field] = updatedData[field];
        }
    });

    // Notificar una sola vez al Event Bus
    updatePatientData({ id: props.data.id, ...updatedData });

    // Cerrar el modal
    showEditModal.value = false;
};

// Función para manejar actualizaciones desde otros componentes (Event Bus)
const handlePatientUpdate = (updatedData) => {
    console.log('🔄 PatientCard: Received update from Event Bus:', updatedData);
    console.log('🔄 PatientCard: Para paciente ID:', props.data.id);

    // Manejar actualización de foto específicamente
    if (updatedData.action === 'patient-photo-updated') {
        console.log('📸 PatientCard: Actualizando photo_url ANTES:', patientData.photo_url);
        patientData.photo_url = updatedData.photo_url;
        console.log('📸 PatientCard: Actualizando photo_url DESPUÉS:', patientData.photo_url);
        return;
    }

    if (updatedData.action === 'patient-photo-deleted') {
        console.log('🗑️ PatientCard: Eliminando photo_url ANTES:', patientData.photo_url);
        patientData.photo_url = null;
        console.log('🗑️ PatientCard: Eliminando photo_url DESPUÉS:', patientData.photo_url);
        return;
    }

    // Solo actualizar los campos básicos que se muestran en la card
    // NO volver a notificar para evitar bucles infinitos
    const fieldsToUpdate = ['first_name', 'last_name', 'gender', 'birthdate', 'email', 'whatsapp', 'photo_url'];
    fieldsToUpdate.forEach(field => {
        if (updatedData[field] !== undefined && updatedData[field] !== patientData[field]) {
            patientData[field] = updatedData[field];
        }
    });
};

// Suscripción al Event Bus
onMounted(() => {
    unsubscribe = patientEventBus.subscribe(props.data.id, handlePatientUpdate);
});

onUnmounted(() => {
    if (unsubscribe) {
        unsubscribe();
    }
});
</script>

<style scoped>
.patient-card {
    animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.card {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.card::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.6s;
}

.card:hover::before {
    left: 100%;
}

.card:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2) !important;
}

.card:active {
    transform: translateY(-4px) scale(1.01);
    transition: all 0.1s;
}

.card-img-top {
    transition: all 0.4s ease;
}

.card:hover .card-img-top {
    transform: scale(1.1) rotate(3deg);
}

.compact-text {
    margin-top: -7px !important;
    transition: color 0.3s ease;
}

.card:hover .compact-text {
    color: #5e72e4;
}

.badge {
    animation: pulse 2s infinite;
}

@keyframes pulse {

    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.1);
    }
}

.icon-container {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-top: 20px;
}

.icon-text {
    cursor: pointer;
    font-size: 18px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
}

.icon-text::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 50%;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #5e72e4, #825ee4);
    transition: all 0.3s ease;
    transform: translateX(-50%);
}

.icon-text:hover {
    transform: translateY(-3px) scale(1.3);
    color: #5e72e4;
}

.icon-text:hover::after {
    width: 100%;
}

.icon-text:active {
    transform: translateY(-1px) scale(1.2);
}

.text-container {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-top: 15px;
    min-height: 24px;
}

.text-container p {
    animation: slideIn 0.4s ease-out;
    margin: 0;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateX(-20px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.text-content {
    opacity: 0;
    transition: opacity 0.5s ease-in-out;
}

.text-content.show {
    opacity: 1;
}

.dropdown-menu {
    animation: fadeInDown 0.3s ease;
}

@keyframes fadeInDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.dropdown-item {
    transition: all 0.2s ease;
}

.dropdown-item:hover {
    transform: translateX(5px);
    background: linear-gradient(90deg, #f8f9fa, #e9ecef);
}

/* Animación para el botón del menú */
.btn-link {
    transition: transform 0.3s ease;
}

.btn-link:hover {
    transform: rotate(90deg);
}

/* Efecto de respiración en el contenedor de imagen */
.position-relative.d-inline-block {
    animation: breathe 3s ease-in-out infinite;
}

@keyframes breathe {

    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.05);
    }
}
</style>
